import React, { useEffect, useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import PuffLoader from "react-spinners/PuffLoader";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ImageIcon, VideoIcon, MessageSquare } from "lucide-react";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchConversations,
  fetchMessages,
  updateIsRead
} from "../../store/partChat/partChatActions";
import PartChatDialog from "@/components/PartChatDialog";
import { useTheme } from "../ThemeProvider";

const RecentMessages = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const { theme } = useTheme();
  const {
    conversations,
    isConversation,
    messages,
    isMessagesLoading,
    isSenderLoading,
    conversationUnsubscribe
  } = useSelector((state) => state.partChat);
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const visibleConversations = conversations?.slice(0, 4) || [];
  const hasMore = conversations?.length > 4;

  const handleConversationClick = (conversation) => {
    setSelectedConversation(conversation);
    dispatch(
      fetchMessages({
        buyerId: user?.uid,
        sellerId: conversation?.otherUserDetail?.id,
        partId: conversation?.partId,
        currentUserId: user?.uid
      })
    );
    dispatch(
      updateIsRead({
        currentUserId: user?.uid,
        receiverId: conversation?.otherUserDetail?.id,
        partId: conversation?.partId
      })
    );
  };

  const getDisplayName = (conversation) => {
    return (
      conversation.otherUserDetail?.companyName ||
      conversation.otherUserDetail?.name ||
      "Unknown User"
    );
  };

  const getAvatarSrc = (conversation) => {
    return (
      conversation.partInfo?.imageUrls?.[0] ||
      conversation.otherUserDetail?.logoUrl ||
      `https://via.placeholder.com/100x100?text=Part`
    );
  };

  const getAvatarFallback = (conversation) => {
    return (
      conversation.partInfo?.name?.charAt(0).toUpperCase() ||
      conversation.otherUserDetail?.name?.charAt(0).toUpperCase() ||
      "P"
    );
  };

  useEffect(() => {
    if (user && isAuthenticated) {
      dispatch(fetchConversations({ userId: user?.uid }));
      return () => {
        if (
          conversationUnsubscribe &&
          typeof conversationUnsubscribe === "function"
        ) {
          conversationUnsubscribe();
        }
      };
    }
  }, [dispatch, user, isAuthenticated]);

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">
              Recent Messages
            </CardTitle>
            <CardDescription>
              Stay connected with your latest buyers.
            </CardDescription>
          </CardHeader>

          <div className="px-4 pb-4 md:min-h-80">
            {isConversation && visibleConversations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10">
                <div className="flex items-center justify-center w-14 h-14 rounded-full bg-muted/30">
                  <PuffLoader
                    color={theme === "dark" ? "#ffffff" : "#1d4ed8"}
                    size={40}
                    margin={4}
                  />
                </div>
                <p className="mt-4 text-sm text-muted-foreground font-medium">
                  Loading Recent Messages...
                </p>
              </div>
            ) : visibleConversations?.length > 0 ? (
              <>
                {visibleConversations.map((conversation, index) => (
                  <motion.div
                    key={
                      conversation.key ||
                      `${conversation.partId}-${conversation.otherUserId}-${index}`
                    }
                    initial={{ opacity: 0, y: 10 }}
                    className="py-1"
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <div
                      onClick={() => handleConversationClick(conversation)}
                      className="flex items-center gap-3 p-3 rounded-lg border hover:shadow-md hover:border-primary transition cursor-pointer mb-3 last:mb-0"
                    >
                      <Avatar className="h-12 w-12 border shrink-0">
                        <AvatarImage
                          src={getAvatarSrc(conversation)}
                          alt={conversation.partData?.name}
                        />
                        <AvatarFallback className="bg-muted text-muted-foreground">
                          {getAvatarFallback(conversation)}
                        </AvatarFallback>
                      </Avatar>

                      {/* Main content */}
                      <div className="flex-grow min-w-0">
                        <div className="flex justify-between items-center">
                          <h3 className="font-semibold text-sm truncate flex items-center gap-2">
                            {conversation?.partInfo?.name
                              ? conversation?.partInfo?.name == "other"
                                ? conversation?.partInfo?.sub_name
                                : conversation?.partInfo?.name
                              : conversation?.partName}
                            {conversation?.count?.[user?.uid] > 0 && (
                              <span className="ml-1 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-500 text-white">
                                {conversation?.count[user.uid]}
                              </span>
                            )}
                          </h3>
                          <span className="text-xs text-muted-foreground">
                            {conversation?.updatedAt?.seconds
                              ? new Date(
                                  conversation.updatedAt.seconds * 1000
                                ).toLocaleDateString()
                              : "Recent"}
                          </span>
                        </div>

                        <p className="text-xs text-muted-foreground mt-1 truncate">
                          {user?.role === "buyer" ? "With: " : "From: "}
                          <span className="font-medium text-foreground">
                            {getDisplayName(conversation)}
                          </span>
                        </p>

                        <p className="text-sm text-muted-foreground mt-1 truncate flex items-center gap-1">
                          {conversation?.lastMessage === "image" ? (
                            <>
                              <ImageIcon className="h-4 w-4" /> Image
                            </>
                          ) : conversation?.lastMessage === "video" ? (
                            <>
                              <VideoIcon className="h-4 w-4" /> Video
                            </>
                          ) : conversation?.lastMessage === "media" ? (
                            <>
                              <ImageIcon className="h-4 w-4" />
                              <VideoIcon className="h-4 w-4" /> Media
                            </>
                          ) : (
                            conversation?.lastMessage || "No messages yet"
                          )}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}

                {hasMore && (
                  <motion.div
                    className="flex justify-center pt-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate("/profile/messages")}
                      className="hover:bg-primary hover:text-primary-foreground transition-colors"
                    >
                      View All Messages ({conversations.length})
                    </Button>
                  </motion.div>
                )}
              </>
            ) : (
              <div className="text-center py-6">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  No new messages.
                </p>
              </div>
            )}
          </div>
        </Card>
      </motion.div>

      {/* Modal */}
      {selectedConversation && (
        <PartChatDialog
          messages={messages}
          conversation={selectedConversation}
          isOpen={!!selectedConversation}
          isMessagesLoading={isMessagesLoading}
          isSenderLoading={isSenderLoading}
          onOpenChange={(open) => {
            if (!open) setSelectedConversation(null);
          }}
        >
          <></>
        </PartChatDialog>
      )}
    </>
  );
};

export default RecentMessages;
